import useWindowSize from '@hooks/useWindowSize';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogHeading
} from 'aquilla/core/molecules/Dialog';
import { SearchInput } from 'aquilla/core/molecules/Search';
import NoResults from 'aquilla/icons/emptyStates/NoResults';
import Cross from 'aquilla/icons/misc/Cross';
import AquillaDrawer from 'aquilla/core/organisms/Drawer';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import useTopology from '@api/hooks/topology/useTopology';
import * as React from 'react';
import CityCentreCard, {
  LeftBlock,
  LeftDiv,
  RightDiv,
  StyH5,
  StyImage,
  StyP1
} from './CityCentreCard';
import { OFFLINE_CENTER_ICONS } from '../constants';

const StyDialogHeader = styled(DialogHeader)`
  padding-bottom: ${Spacings.SPACING_4B};
`;

const Container = styled.div`
  padding: ${Spacings.SPACING_4B};
  padding-right: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_4B};
  grid-column: span 6;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
`;

const SubHeading = styled(Typography)`
  display: ${({ $hide }) => ($hide ? 'none' : '')};
`;

const GoalName = styled.span`
  color: var(--color-text-primary);
`;

const NoCitiesWrapper = styled.div`
  max-width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
`;
const NoCitiesContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 340px;
  height: 100%;
  width: 100%;
`;
const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
  margin-top: ${Spacings.SPACING_4B};
  text-align: center;
`;

const SubTitle = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
  color: var(--color-text-secondary);
  text-align: 'center';
`;

const StyAquillaDrawer = styled(AquillaDrawer)`
  margin-top: ${Spacings.SPACING_18B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_16B};
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: 10px;
    }
    & > .aquilla-drawer-content {
      padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    }
    max-height: 90vh;
  }
`;
const StyDialog = styled(Dialog)`
  margin-top: ${Spacings.SPACING_18B};
  max-height: 82vh;
  min-height: 65vh;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_16B};
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: 10px;
    }
    & > .aquilla-drawer-content {
      padding: ${Spacings.SPACING_4B};
      padding-bottom: ${Spacings.SPACING_0B};
    }
    max-height: 90vh;
  }
`;

const SearchWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    width: 100%;
  }
`;

const DrawerWrapper = styled.div`
  width: 100%;
  align-items: center;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin: auto;
  justify-content: center;
  flex-direction: column;
  padding-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    display: flex;
    width: 100%;
    justify-content: left;
    height: calc(100vh - 120px);
    overflow-y: scroll;
    margin-top: 58px;
    padding-bottom: 0px;
  }
`;

const SelectCentreWrapper = styled.div`
  grid-column: span 24;
  display: flex;
  justify-content: center;
  margin-bottom: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    justify-content: left;
    width: 100%;
    position: absolute;
    z-index: 1;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_2B};
    top: 0;
    background: var(--color-base-1);
    border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
  }
`;
const SelectCentreH2 = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const CentreSelectionDrawer = ({
  goalUID,
  isCitiesDrawerOpen,
  setIsCitiesDrawerOpen,
  cityData
}) => {
  const [searchInput, setSearchInput] = React.useState('');

  const closeCentreListDrawer = () => {
    setTimeout(() => {
      setIsCitiesDrawerOpen(false);
      document.body.style.overflow = 'unset';
    }, 250);
  };

  const getCentresListToBeRendered = () => {
    let cities = cityData;
    if (!cityData?.length) return null;

    if (searchInput) {
      cities = cities.filter((city) => {
        return (
          city.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
          city.state?.toLowerCase().includes(searchInput.toLowerCase())
        );
      });
    }
    return cities;
  };

  const renderNoCitiesFound = () => {
    const centres = getCentresListToBeRendered();
    if (centres?.length) return null;
    return (
      <NoCitiesContainer>
        <NoCitiesWrapper>
          <NoResults />
          <Title variant="h4">Sorry we are not there yet</Title>
          <SubTitle variant="p2">
            We are coming to a city near you soon!
          </SubTitle>
        </NoCitiesWrapper>
      </NoCitiesContainer>
    );
  };

  const renderGoalCentresOfACity = () => {
    const cities = getCentresListToBeRendered();
    if (!cities?.length) return null;
    const cityCards = () => {
      return (
        <>
          {cities.map((centre) => {
            return (
              <CityCentreCard
                data={centre}
                goalUID={goalUID}
                key={centre.uid}
                closeDrawer={closeCentreListDrawer}
              />
            );
          })}
          <Container>
            <LeftBlock>
              <LeftDiv>
                <StyImage
                  src={OFFLINE_CENTER_ICONS?.viewAll}
                  alt="city-icon"
                  height="48px"
                  width="48px"
                />
              </LeftDiv>
              <RightDiv>
                <StyH5 variant="h5">Coming soon</StyH5>
                <StyP1 variant="p2">in more cities</StyP1>
              </RightDiv>
            </LeftBlock>
          </Container>
        </>
      );
    };
    return cityCards();
  };

  const [hideSubHeading, setHideSubHeading] = React.useState(false);

  const hideSubHeadingOnScroll = () => {
    const contentElement = document.querySelector('#dialog-content');
    if (contentElement.scrollTop > 50) return setHideSubHeading(true);
    return setHideSubHeading(false);
  };

  const { isMobile } = useWindowSize();

  const handleOnChangeSearch = (changedValue) => {
    setSearchInput(changedValue);
  };

  const handleOnClearSearch = () => {
    setSearchInput('');
  };

  const getCentresLength = () => {
    if (!cityData?.length) return null;
    return cityData?.reduce((total, city) => {
      return total + city?.centres?.length || 0;
    }, 0);
  };
  const { topology: goalTopology } = useTopology(goalUID);
  const { name: goalName } = goalTopology;
  return (
    <>
      {!isMobile ? (
        <StyDialog
          open={isCitiesDrawerOpen}
          size="medium"
          close={closeCentreListDrawer}
        >
          <StyDialogHeader>
            <Cross onClick={closeCentreListDrawer} height={24} width={24} />
            <DialogHeading>Select a centre</DialogHeading>
            <SubHeading
              variant="h6"
              color="var(--color-text-secondary)"
              $hide={hideSubHeading}
            >
              {getCentresLength() || 0} centres available around{' '}
              {cityData?.length || 0} cities in India for{' '}
              <GoalName>
                {goalName || 'IIT JEE, NEET UG and Foundation'}{' '}
              </GoalName>
              preparation
            </SubHeading>
          </StyDialogHeader>
          <DialogContent onScroll={hideSubHeadingOnScroll} id="dialog-content">
            <SearchWrapper>
              <SearchInput
                value={searchInput}
                onChange={handleOnChangeSearch}
                onClear={handleOnClearSearch}
                placeholder="Search for cities and states"
              />
            </SearchWrapper>
            <DrawerWrapper>{renderGoalCentresOfACity()}</DrawerWrapper>
            {renderNoCitiesFound()}
          </DialogContent>
        </StyDialog>
      ) : (
        <StyAquillaDrawer
          anchor="top"
          open={isCitiesDrawerOpen}
          onClose={closeCentreListDrawer}
          showCloseIcon={!isMobile}
        >
          <DrawerWrapper>
            <SelectCentreWrapper>
              <Cross onClick={closeCentreListDrawer} height={24} width={24} />
              <SelectCentreH2 variant="h5">Select a centre</SelectCentreH2>
            </SelectCentreWrapper>
            <SearchWrapper>
              <SearchInput
                value={searchInput}
                onChange={handleOnChangeSearch}
                onClear={handleOnClearSearch}
                placeholder="Search for cities and states"
              />
            </SearchWrapper>
            {renderGoalCentresOfACity()}
            {renderNoCitiesFound()}
          </DrawerWrapper>
        </StyAquillaDrawer>
      )}
    </>
  );
};

export default CentreSelectionDrawer;
